import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Carousel, Spinner } from 'react-bootstrap';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/i18n';
import './Landing.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

function getPostUrl(post) {
  if (!post?.slug) return '#';
  const lang = post.language === 'en' ? 'en' : 'es';
  const base = lang === 'en' ? '/news' : '/noticias';
  const paths = {
    en: { news: 'news', tech: 'tech', business: 'business', movie: 'business', series: 'tech', music: 'news' },
    es: { news: 'actualidad', tech: 'tecnologia', business: 'negocios', movie: 'negocios', series: 'tecnologia', music: 'actualidad' }
  };
  const categoryPath = paths[lang][post.category] || paths[lang].news;
  return `${base}/${categoryPath}/${post.slug}`;
}

/**
 * Carrusel reutilizable de noticias aleatorias según el idioma.
 * Se puede usar en cualquier página (post, landing, etc.).
 *
 * @param {number} [excludePostId] - ID del post a excluir (ej. el post actual)
 * @param {number} [limit=10] - Número de noticias a mostrar
 * @param {string} [titleKey='common.blog.mightAlsoLike'] - Clave i18n del título de la sección
 * @param {string} [className] - Clases CSS adicionales para el contenedor
 */
const MoreNewsCarousel = ({ excludePostId, limit = 10, titleKey = 'common.blog.mightAlsoLike', className = '' }) => {
  const { language } = useLanguage();
  const lang = language === 'en' ? 'en' : 'es';
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    params.set('language', lang);
    params.set('limit', String(limit));
    params.set('order', 'random');
    if (excludePostId != null) params.set('excludeId', String(excludePostId));
    fetch(`${API_BASE}/api/blog/posts?${params}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { posts: [] }))
      .then((data) => {
        if (!cancelled && Array.isArray(data.posts)) setPosts(data.posts);
      })
      .catch(() => { if (!cancelled) setPosts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lang, limit, excludePostId]);

  if (loading) {
    return (
      <section className={`landing-news landing-news-section py-4 py-md-5 ${className}`.trim()}>
        <Container>
          <h2 className="landing-news-heading text-center mb-4">{t(titleKey, language)}</h2>
          <div className="text-center py-4">
            <Spinner animation="border" variant="light" />
          </div>
        </Container>
      </section>
    );
  }

  if (posts.length === 0) return null;

  return (
    <section className={`landing-news landing-news-section py-4 py-md-5 ${className}`.trim()}>
      <Container>
        <h2 className="landing-news-heading text-center mb-4">{t(titleKey, language)}</h2>
        <Carousel indicators controls className="landing-news-carousel">
          {posts.map((post) => (
            <Carousel.Item key={post.id}>
              <Link to={getPostUrl(post)} className="landing-news-slide-link text-decoration-none d-block">
                <div className="landing-news-slide-card row align-items-stretch g-0 overflow-hidden rounded-4">
                  <div className="col-md-5 landing-news-slide-media">
                    {post.featured_image ? (
                      <img
                        src={post.featured_image.startsWith('http') ? post.featured_image : API_BASE + post.featured_image}
                        alt=""
                        className="w-100 h-100 object-fit-cover"
                        style={{ minHeight: 220, maxHeight: 280, objectFit: 'cover' }}
                      />
                    ) : (
                      <div className="landing-news-slide-placeholder d-flex align-items-center justify-content-center" style={{ minHeight: 220 }}>
                        <span className="landing-news-slide-placeholder-icon" aria-hidden>
                          📰
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="col-md-7 landing-news-slide-body p-4">
                    <h3 className="landing-news-slide-title mb-2">{post.title}</h3>
                    <p className="landing-news-slide-excerpt small mb-0 line-clamp-3">{post.excerpt || post.title}</p>
                    <span className="landing-news-slide-cta small mt-3 d-inline-block">{t('landing.latestNewsReadMore', language)} →</span>
                  </div>
                </div>
              </Link>
            </Carousel.Item>
          ))}
        </Carousel>
      </Container>
    </section>
  );
};

export default MoreNewsCarousel;
